import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { useApi } from '../../context/ApiContext';

export default function ApplicantProfile(){
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminGetApplicant, uploadFile, adminAddApplicantDocument, adminAssignUnitToApplicant, adminUnassignUnitFromApplicant, adminApproveApplicant, adminGetUnit, adminListUnits, adminSendLease, adminGetWebsiteSettings } = useApi();
  const [units, setUnits] = useState([]);
  const [noAvailable, setNoAvailable] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Default to "unit" tab when the applicant has no unit yet so the assignment
  // UI is visible immediately. Otherwise start on overview.
  const [tab, setTab] = useState('overview');
  useEffect(() => {
    if (item && !item.unitId) setTab('unit');
  }, [item?.id, item?.unitId]);

  // Lease confirmation modal state
  const assignedUnit = useMemo(
    () => (item?.unitId ? units.find((u) => u.id === item.unitId) : null),
    [units, item?.unitId]
  );
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [leaseForm, setLeaseForm] = useState({ rentAmount: '', depositAmount: '', startsOn: '', endsOn: '', notes: '' });
  const [approving, setApproving] = useState(false);
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const openLeaseModal = () => {
    setLeaseForm({
      rentAmount: assignedUnit?.price ? String(assignedUnit.price) : '',
      depositAmount: assignedUnit?.depositAmount != null ? String(assignedUnit.depositAmount) : (assignedUnit?.price ? String(assignedUnit.price) : ''),
      startsOn: item?.desiredMoveInDate || todayIso(),
      endsOn: '',
      notes: '',
    });
    setShowLeaseModal(true);
  };
  const onLeaseChange = (e) => {
    const { name, value } = e.target;
    setLeaseForm((f) => ({ ...f, [name]: value }));
  };
  const submitLease = async () => {
    try {
      setApproving(true);
      await adminApproveApplicant({
        appId: id,
        unitId: item.unitId,
        rentAmount: leaseForm.rentAmount === '' ? undefined : Number(leaseForm.rentAmount),
        depositAmount: leaseForm.depositAmount === '' ? undefined : Number(leaseForm.depositAmount),
        startsOn: leaseForm.startsOn || undefined,
        endsOn: leaseForm.endsOn || undefined,
        notes: leaseForm.notes || undefined,
      });
      const data = await adminGetApplicant(id);
      setItem(data || null);
      setShowLeaseModal(false);
    } catch (e) {
      alert(e?.message || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  // Send/Resend lease modal — pulls defaults from /admin/website-settings,
  // lets the admin tweak subject/body and attach a file, then posts to the
  // send-lease endpoint which dispatches via Mailgun.
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({ subject: '', body: '', cc: '', bcc: '' });
  const [sendFile, setSendFile] = useState(null);
  const [mergeFields, setMergeFields] = useState([]);
  const isApproved = ['approved', 'converted'].includes((item?.status || '').toLowerCase());
  const openSendModal = async () => {
    let defaults = { subject: '', body: '', cc: '', bcc: '' };
    let merge = [];
    try {
      const ws = await adminGetWebsiteSettings();
      const le = ws?.leaseEmail || {};
      defaults = {
        subject: le.subject || '',
        body: le.body || '',
        cc: le.cc || '',
        bcc: le.bcc || '',
      };
      merge = Array.isArray(le.mergeFields) ? le.mergeFields : [];
    } catch (_) {}
    setSendForm(defaults);
    setMergeFields(merge);
    setSendFile(null);
    setShowSendModal(true);
  };
  const onSendChange = (e) => {
    const { name, value } = e.target;
    setSendForm((f) => ({ ...f, [name]: value }));
  };
  const submitSend = async () => {
    try {
      setSending(true);
      let attachmentUrl;
      let attachmentName;
      if (sendFile) {
        const up = await uploadFile(sendFile, { folder: `leases/${id}` });
        attachmentUrl = up?.url;
        attachmentName = sendFile.name;
      }
      await adminSendLease({
        appId: id,
        subject: sendForm.subject || undefined,
        body: sendForm.body || undefined,
        cc: sendForm.cc || undefined,
        bcc: sendForm.bcc || undefined,
        attachmentUrl,
        attachmentName,
      });
      setShowSendModal(false);
      alert('Lease email sent.');
    } catch (e) {
      alert(e?.message || 'Failed to send lease email');
    } finally {
      setSending(false);
    }
  };
  const TabButton = ({ id, children }) => (
    <button
      className={`px-3 py-2 text-sm border-b-2 ${tab===id ? 'border-orange-600 text-orange-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
      onClick={()=>setTab(id)}
      type="button"
    >{children}</button>
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError('');
        const data = await adminGetApplicant(id);
        setItem(data || null);
      } catch(e){ setError(e?.message || 'Failed to load applicant'); }
      finally { setLoading(false); }
    })();
  }, [id, adminGetApplicant]);

  useEffect(() => {
    (async () => {
      try {
        if (!item?.propertySlug) return;
        // Admin endpoint returns ALL units (vacant + reserved + occupied + maintenance + offline);
        // filter on the client so we can show vacant first and gray out the rest.
        let list = [];
        try {
          const all = await adminListUnits(item.propertySlug);
          list = Array.isArray(all) ? all : [];
        } catch {}
        const vacant = list.filter(u => u.available);
        setNoAvailable(vacant.length === 0);
        if (vacant.length > 0) {
          // Sort vacant first
          list = [...vacant, ...list.filter(u => !u.available)];
        }
        // If an assigned unit exists but is not available, include it explicitly
        if (item.unitId && !list.find(u => u.id === item.unitId)) {
          try {
            const unit = await adminGetUnit(item.unitId);
            if (unit) list = [unit, ...list];
          } catch {}
        }
        setUnits(list);
      } catch {}
    })();
  }, [item?.propertySlug, item?.unitId]);

  if (loading) return <div className="p-6">Loading…</div>;
  if (error || !item) return (
    <div className="p-6">
      <div className="mb-3"><button className="inline-flex items-center gap-2" onClick={()=>navigate(-1)}><ArrowLeft className="w-4 h-4"/> Back</button></div>
      <div className="text-red-700">{error || 'Not found'}</div>
    </div>
  );

  const docs = item.documents || [];
  const avatarUrl = (() => {
    if (item.profilePhoto) return item.profilePhoto;
    const byName = docs.find(d => typeof d?.name === 'string' && /profile/i.test(d.name) && d.url);
    if (byName?.url) return byName.url;
    const byExt = docs.find(d => (d?.fileName || d?.url || '').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url);
    return byExt?.url || '';
  })();

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <button className="inline-flex items-center gap-2" onClick={()=>navigate(-1)}><ArrowLeft className="w-4 h-4"/> Back</button>
        <div className="text-xs text-gray-600">ID: {item.id}</div>
      </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {avatarUrl ? (
              <img src={avatarUrl} alt={item.fullName || ''} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200" />
            )}
            <div>
              <div className="text-2xl font-bold">{item.fullName || item.form?.fullName}</div>
              <div className="text-gray-700">{item.email || item.form?.email} • {item.phone || item.form?.phone}</div>
              <div className="text-sm text-gray-600">Property: {item.propertySlug} • Unit: {item.unitId || '—'}</div>
              <div className="text-sm text-gray-600">Status: {item.status || 'Submitted'} • Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              className="px-3 py-2 border rounded bg-orange-600 text-white disabled:opacity-50"
              disabled={!item.unitId || approving || ['approved','converted','declined'].includes((item.status || '').toLowerCase())}
              title={!item.unitId ? 'Assign a unit first (Unit tab)' : 'Review lease terms, then approve'}
              onClick={openLeaseModal}
            >Approve</button>
            {!item.unitId && (
              <button
                className="text-xs text-orange-700 underline"
              onClick={() => setTab('unit')}
            >Assign a unit first →</button>
          )}
          {isApproved && (
            <button
              className="px-3 py-2 border rounded text-sm disabled:opacity-50"
              disabled={sending || !item.email}
              title={!item.email ? 'Applicant has no email address on file' : 'Open the lease email composer'}
              onClick={openSendModal}
              type="button"
            >Send/Resend Lease</button>
          )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b flex items-center gap-2">
          <TabButton id="overview">Overview</TabButton>
          <TabButton id="unit">Unit</TabButton>
          <TabButton id="documents">Documents</TabButton>
          <TabButton id="timeline">Timeline</TabButton>
        </div>

        {/* Unit Assignment */}
        {tab==='unit' && (
          <div className="mt-4 border rounded p-4">
            <h2 className="font-semibold mb-2">Unit Assignment</h2>
            <div className="text-sm text-gray-700 mb-2">Current unit: {item.unitId || '—'}</div>
            {item.unitId && (
              <button className="px-2 py-1 border rounded text-xs mb-2" onClick={async()=>{
                try { await adminUnassignUnitFromApplicant({ appId: id }); const data=await adminGetApplicant(id); setItem(data||null);} catch(e){ alert(e?.message||'Failed to unassign'); }
              }}>Unassign</button>
            )}
            {noAvailable && units.length > 0 && (
              <div className="mb-2 p-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded">
                No available units for this property right now. All units are shown below for reference; occupied units are disabled.
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {units.length === 0 && <div className="text-sm text-gray-500">No units found for this property. Create a unit under Admin → Units.</div>}
              {units.map(u => (
                <button key={u.id} disabled={assigning || ((!u.available || (u.reservedByAppId && u.reservedByAppId !== id)) && u.id !== item.unitId)} title={(!u.available || (u.reservedByAppId && u.reservedByAppId !== id)) && u.id !== item.unitId ? 'Unit not available' : ''} onClick={async()=>{
                  try {
                    setAssigning(true);
                    await adminAssignUnitToApplicant({ appId: id, unitId: u.id });
                    const data = await adminGetApplicant(id);
                    setItem(data || null);
                  } catch (e) { alert(e?.message || 'Failed to assign'); }
                  finally { setAssigning(false); }
                }} className={`px-3 py-2 border rounded ${item.unitId===u.id ? 'bg-orange-50 border-orange-300' : ''}`}>
                  {u.name || u.id} {u.available ? '' : '(occupied)'} {u.reservedByAppId && u.reservedByAppId !== id ? '(reserved)' : ''}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-2">Choose a unit to assign to this applicant. The unit will be marked occupied automatically on approval.</div>
          </div>
        )}

        {/* Overview */}
        {tab==='overview' && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <section className="border rounded p-4">
              <h2 className="font-semibold mb-2">Overview</h2>
              <div className="text-sm text-gray-700">Property: {item.propertySlug} • Unit: {item.unitId || '—'}</div>
              <div className="text-sm text-gray-700">Status: {item.status || 'Submitted'}</div>
              <div className="text-sm text-gray-700">Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}</div>
            </section>
            <section className="border rounded p-4">
              <h2 className="font-semibold mb-2">Notes</h2>
              <div className="text-sm text-gray-500">Coming soon…</div>
            </section>
          </div>
        )}

        {/* Documents */}
        {tab==='documents' && (
          <section className="mt-4 border rounded p-4">
            <h2 className="font-semibold mb-2">Documents</h2>
            <div className="mb-3 flex items-center gap-2">
              <input className="border rounded px-2 py-1" placeholder="Document name" value={docName} onChange={(e)=>setDocName(e.target.value)} />
              <input type="file" onChange={(e)=>setDocFile(e.target.files?.[0]||null)} />
              <button type="button" disabled={saving || !docFile} className="px-3 py-1.5 border rounded disabled:opacity-50" onClick={async()=>{
                if (!docFile) return;
                try {
                  setSaving(true);
                  setUploadError('');
                  const up = await uploadFile(docFile, { folder: `applications/${id}` });
                  await adminAddApplicantDocument({ appId: id, document: { name: docName || docFile.name, fileName: docFile.name, url: up.url } });
                  const data = await adminGetApplicant(id);
                  setItem(data || null);
                  setDocName(''); setDocFile(null);
                } catch (e) {
                  setUploadError(e?.message || 'Upload failed');
                  alert(e?.message || 'Upload failed');
                } finally { setSaving(false); }
              }}>Upload</button>
            </div>
            {uploadError && <div className="text-xs text-red-600 mb-2">{uploadError}</div>}
            <ul className="text-sm space-y-2">
              {docs.map((d, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{d.name || 'Document'}</div>
                    <div className="text-xs text-gray-600 break-all">{d.fileName || d.url || ''}</div>
                  </div>
                  {d.url ? (
                    <div className="flex items-center gap-2">
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">View</a>
                      <a href={d.url} download className="text-gray-700 border px-2 py-1 rounded text-xs">Download</a>
                    </div>
                  ) : <span className="text-xs text-gray-500">No file</span>}
                </li>
              ))}
              {docs.length === 0 && (<li className="text-gray-500">No documents</li>)}
            </ul>
          </section>
        )}

        {/* Timeline */}
        {tab==='timeline' && (
          <section className="mt-4 border rounded p-4">
            <h2 className="font-semibold mb-2">Timeline</h2>
            <ul className="text-sm">
              {(item.events || []).slice().reverse().map(ev => (
                <li key={ev.id} className="py-1"><span className="text-gray-500">{new Date(ev.at).toLocaleString()}:</span> {ev.label}</li>
              ))}
              {(item.events || []).length === 0 && <li className="text-gray-500">No events</li>}
            </ul>
          </section>
        )}
      </div>

      {/* Lease confirmation modal — opens when clicking Approve so the user can
          review/edit rent, deposit, start date and add notes before the lease
          and tenancy are created. */}
      {showLeaseModal && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => !approving && setShowLeaseModal(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Confirm Lease & Approve</h2>
              <button className="border rounded p-1" onClick={() => !approving && setShowLeaseModal(false)} disabled={approving}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4 space-y-1">
              <div><span className="text-gray-500">Applicant:</span> {item.fullName}</div>
              <div><span className="text-gray-500">Property:</span> {item.propertySlug}</div>
              <div><span className="text-gray-500">Unit:</span> {assignedUnit?.code || assignedUnit?.name || item.unitId}</div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Monthly rent (R)</label>
                <input
                  type="number" min="0" step="any" name="rentAmount" value={leaseForm.rentAmount}
                  onChange={onLeaseChange}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Pre-filled from the unit ({assignedUnit?.price ? `R${Number(assignedUnit.price).toLocaleString()}` : '—'}). Edit if this lease has different terms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Deposit (R)</label>
                <input
                  type="number" min="0" step="any" name="depositAmount" value={leaseForm.depositAmount}
                  onChange={onLeaseChange}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start date</label>
                  <input
                    type="date" name="startsOn" value={leaseForm.startsOn}
                    onChange={onLeaseChange}
                    className="mt-1 w-full border rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End date <span className="text-gray-400 text-xs">(optional)</span></label>
                  <input
                    type="date" name="endsOn" value={leaseForm.endsOn}
                    onChange={onLeaseChange}
                    className="mt-1 w-full border rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes <span className="text-gray-400 text-xs">(optional)</span></label>
                <textarea
                  name="notes" value={leaseForm.notes} onChange={onLeaseChange}
                  rows={3}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  placeholder="Any special terms or remarks; saved to the application's internal notes."
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowLeaseModal(false)}
                disabled={approving}
                className="px-3 py-2 border rounded disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={submitLease}
                disabled={approving}
                className="px-3 py-2 bg-orange-600 text-white rounded disabled:opacity-50"
              >{approving ? 'Creating lease…' : 'Create Lease & Approve'}</button>
            </div>
          </div>
        </div>
      )}

      {showSendModal && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => !sending && setShowSendModal(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Send / Resend Lease</h2>
                <p className="text-sm text-gray-600">This email will be sent to {item.email || 'the applicant email on file'}.</p>
              </div>
              <button className="border rounded p-1" onClick={() => !sending && setShowSendModal(false)} disabled={sending} type="button">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid gap-4">
              <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-600">
                <div className="rounded-lg border bg-gray-50 px-3 py-2">
                  <div className="text-gray-500">Applicant</div>
                  <div className="font-medium text-gray-900">{item.fullName || '—'}</div>
                </div>
                <div className="rounded-lg border bg-gray-50 px-3 py-2">
                  <div className="text-gray-500">Unit</div>
                  <div className="font-medium text-gray-900">{assignedUnit?.code || assignedUnit?.name || item.unitId || '—'}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Subject</label>
                <input
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  name="subject"
                  value={sendForm.subject}
                  onChange={onSendChange}
                  placeholder="Lease ready for {{applicantName}}"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">CC <span className="text-gray-400 text-xs">(optional)</span></label>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    name="cc"
                    value={sendForm.cc}
                    onChange={onSendChange}
                    placeholder="ops@example.com, manager@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">BCC <span className="text-gray-400 text-xs">(optional)</span></label>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    name="bcc"
                    value={sendForm.bcc}
                    onChange={onSendChange}
                    placeholder="audit@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Body</label>
                <textarea
                  className="mt-1 w-full border rounded-md px-3 py-2 font-mono text-sm"
                  name="body"
                  value={sendForm.body}
                  onChange={onSendChange}
                  rows={12}
                  placeholder="Hi {{applicantName}},"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Attachment <span className="text-gray-400 text-xs">(optional)</span></label>
                <input
                  className="mt-1 block w-full text-sm"
                  type="file"
                  onChange={(e) => setSendFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-500 mt-1">Choose the lease document you want attached to the email.</p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <div className="text-sm font-medium text-gray-900 mb-2">Available merge fields</div>
                <div className="flex flex-wrap gap-2">
                  {mergeFields.map((field) => (
                    <span key={field} className="rounded-full border bg-white px-2 py-1 text-xs text-gray-700">
                      {`{{${field}}}`}
                    </span>
                  ))}
                  {mergeFields.length === 0 && (
                    <span className="text-xs text-gray-500">No merge fields returned from the server.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowSendModal(false)}
                disabled={sending}
                className="px-3 py-2 border rounded disabled:opacity-50"
                type="button"
              >Cancel</button>
              <button
                onClick={submitSend}
                disabled={sending || !item.email}
                className="px-3 py-2 bg-orange-600 text-white rounded disabled:opacity-50"
                type="button"
              >{sending ? 'Sending…' : 'Send Lease Email'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
